import React, {Component} from 'react';
import Drawer from '@material-ui/core/Drawer';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import Chip from '@material-ui/core/Chip';
import Alert from '@material-ui/lab/Alert';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Button from '../../../components/button';

/**
 * Mockup screen S6's "Delegate duties" drawer, and S7's nomination form — one component, because
 * the two differ by exactly one field.
 *
 * THE FORM MAKES YOU PICK THE DUTIES. §12.2 is explicit that "cover my Benefits work" and "cover
 * everything I can do" are different requests, and the second quietly includes any confidential
 * sight or other-team access the delegator happens to hold. There is no "select all" here, by
 * design — the mockup's own caption is "Required — there is no 'all'".
 *
 * NON-DELEGATABLE DUTIES ARE SHOWN DISABLED WITH THE REASON, never hidden. Hiding produces a
 * support ticket; showing answers it in place.
 */
class DelegateDrawer extends Component {
  constructor(props) {
    super(props);
    this.state = this.emptyForm();
  }

  emptyForm() {
    return {delegate: null, selected: {}, reason: '', expiresAt: '', filter: ''};
  }

  /**
   * The duties to render, narrowed by the filter box.
   *
   * THE FILTER EXISTS BECAUSE OF WHAT REAL DATA LOOKS LIKE. The mockup draws a steward with four
   * duties; the seeded steward holds **96** (permission, scope) pairs in their own right — a
   * global `data_steward` role is ~50 codes on its own, and ownership of one schema adds a
   * `data_owner`'s worth more. Ninety-six checkboxes is not a chooser, it is a wall, and §12.2's
   * whole point is that the delegator makes a deliberate choice.
   *
   * Found by opening the screen against seeded data, not by any test: every fixture in the suite
   * supplies two pairs.
   *
   * @return {Array<object>} The visible subset, in the order the server sent them.
   */
  visiblePairs() {
    const needle = this.state.filter.trim().toLowerCase();
    const all = this.props.pairs || [];
    if (!needle) return all;
    return all.filter((p) =>
      `${p.code} ${p.description || ''} ${p.scopeLabel || ''} ${p.scope.type}`
          .toLowerCase().includes(needle));
  }

  // The drawer is kept mounted and toggled by `open`, so without this a second delegation opens
  // holding the first one's selections — the same class of defect ClassificationPanel's
  // componentDidUpdate guard exists for, and it would silently lend the wrong duties.
  componentDidUpdate(prevProps) {
    if (prevProps.open && !this.props.open) this.setState(this.emptyForm());
  }

  togglePair = (key) => () => {
    this.setState((s) => ({selected: {...s.selected, [key]: !s.selected[key]}}));
  };

  keyOf = (pair) => `${pair.code}|${pair.scope.type}:${pair.scope.id || ''}`;

  chosenPairs() {
    return (this.props.pairs || [])
        .filter((p) => p.delegatable && this.state.selected[this.keyOf(p)])
        .map((p) => ({code: p.code, scope: p.scope}));
  }

  handleSubmit = () => {
    const pairs = this.chosenPairs();
    if (!pairs.length || !this.state.delegate) return;
    const payload = {
      delegateSubjectId: this.state.delegate.subjectId,
      pairs,
      reason: this.state.reason.trim() || null,
    };
    // A nomination carries no end date: §12.4 starts the window at activation, because an absence
    // nobody can schedule cannot have its cover scheduled either.
    if (this.props.kind !== 'standing_delegation') {
      payload.expiresAt = this.state.expiresAt ? `${this.state.expiresAt}T23:59:59` : null;
    }
    this.props.onSubmit(payload, this.props.kind);
  };

  renderDuty(pair) {
    const key = this.keyOf(pair);
    return (
      <label
        key={key}
        className={`duty-row${pair.delegatable ? '' : ' duty-row-disabled'}`}
        data-testid="ac-duty-row"
        data-code={pair.code}
        data-delegatable={pair.delegatable ? 'yes' : 'no'}
      >
        <Checkbox
          size="small"
          checked={Boolean(this.state.selected[key])}
          disabled={!pair.delegatable}
          onChange={this.togglePair(key)}
          inputProps={{'data-testid': 'ac-duty-check', 'data-code': pair.code}}
        />
        <span className="duty-body">
          <span className="duty-title">{pair.description || pair.code}</span>
          <span className="duty-scope">
            {pair.scope.type === 'global' ?
              'Everywhere' :
              /* A withheld name renders as (restricted), never the raw id — the scope resolver
                 omits ids the caller's own §7.1 filter excluded. */
              `${pair.scope.type} · ${pair.scopeLabel || '(restricted)'}`}
          </span>
          {!pair.delegatable && (
            <span className="duty-reason" data-testid="ac-duty-reason">
              Cannot be delegated — {pair.reason}
            </span>
          )}
        </span>
      </label>
    );
  }

  render() {
    const {open, kind, delegates, pairs, error, onClose} = this.props;
    const {delegate, reason, expiresAt} = this.state;
    const nomination = kind === 'standing_delegation';
    const chosen = this.chosenPairs();
    const canSubmit = Boolean(delegate) && chosen.length > 0 && (nomination || Boolean(expiresAt));
    const delegatable = (pairs || []).filter((p) => p.delegatable);
    const visible = this.visiblePairs();

    return (
      <Drawer anchor="right" open={open} onClose={onClose}
        PaperProps={{'className': 'ac-delegate-drawer', 'data-testid': 'ac-delegate-drawer'}}>
        <div className="drawer-inner">
          <h3>{nomination ? 'Name a standing backup' : 'Delegate duties'}</h3>
          <p className="drawer-sub">
            {nomination ?
              'This sits dormant and grants nothing until someone activates it.' :
              'You keep everything you delegate. Delegation adds to someone else’s access; it never removes yours.'}
          </p>

          <Autocomplete
            options={delegates || []}
            getOptionLabel={(d) => d.displayName || ''}
            value={delegate}
            onChange={(e, v) => this.setState({delegate: v})}
            renderInput={(params) => (
              <TextField {...params} label={nomination ? 'Backup' : 'To'} variant="standard"
                inputProps={{...params.inputProps, 'data-testid': 'ac-delegate-picker'}} />
            )}
          />

          <div className="duties-block">
            <div className="duties-head">
              Which duties? <span className="duties-required">Required — there is no “all”</span>
            </div>

            {/* §3.3's cost, made legible. A delegator whose grants all arrive through a group has
                an EMPTY delegable list, and "no duties" without a reason reads as a broken page. */}
            {!delegatable.length ? (
              <div className="duties-empty" data-testid="ac-duties-empty">
                Nothing you hold in your own right can be delegated. Access that reaches you through
                a team stays with the team — you may lend what is granted to you, not what is granted
                to a room you stand in.
              </div>
            ) : (
              <>
                {/* A real steward holds ~96 pairs, not the four the mockup draws. Without this the
                    chooser is a wall, which defeats the deliberate choice §12.2 requires. */}
                {(pairs || []).length > 12 && (
                  <TextField
                    className="duties-filter"
                    placeholder={`Filter ${pairs.length} duties…`}
                    value={this.state.filter}
                    onChange={(e) => this.setState({filter: e.target.value})}
                    inputProps={{'data-testid': 'ac-duty-filter'}}
                  />
                )}
                {visible.map((p) => this.renderDuty(p))}
                {!visible.length && (
                  <div className="duties-empty" data-testid="ac-duties-no-match">
                    No duty matches “{this.state.filter}”.
                  </div>
                )}
                {/* Selections survive filtering — narrowing the view must never silently drop a
                    duty the delegator already ticked. Stated on screen so they can trust it. */}
                {chosen.length > 0 && visible.length < (pairs || []).length && (
                  <div className="duties-kept" data-testid="ac-duties-kept">
                    {chosen.length} selected, including any hidden by the filter.
                  </div>
                )}
              </>
            )}
          </div>

          {!nomination && (
            <TextField
              className="drawer-field"
              label="Until"
              type="date"
              required
              value={expiresAt}
              onChange={(e) => this.setState({expiresAt: e.target.value})}
              InputLabelProps={{shrink: true}}
              helperText="Maximum 90 days, and never longer than your own access lasts."
              inputProps={{'data-testid': 'ac-delegate-until'}}
            />
          )}

          <TextField
            className="drawer-field"
            label="Reason"
            value={reason}
            onChange={(e) => this.setState({reason: e.target.value})}
            placeholder="Vacation, workload, other"
            inputProps={{'maxLength': 255, 'data-testid': 'ac-delegate-reason'}}
          />

          {chosen.length > 0 && (
            <div className="drawer-preview" data-testid="ac-delegate-preview">
              <b>{chosen.length}</b> {chosen.length === 1 ? 'duty' : 'duties'} will be lent
              {delegate ? <> to <b>{delegate.displayName}</b></> : null}.
              {' '}Everything they do under it reads “{delegate ? delegate.displayName : 'they'}, acting for you”.
            </div>
          )}

          {error && <Alert severity="error" data-testid="ac-delegate-error">{error}</Alert>}

          <div className="drawer-actions">
            <Button label="Cancel" className="btn btn-default" onClick={onClose}
              data-testid="ac-delegate-cancel" />
            <Button label={nomination ? 'Save nomination' : 'Delegate'} className="btn btn-action"
              disabled={!canSubmit} onClick={this.handleSubmit} data-testid="ac-delegate-submit" />
          </div>

          <div className="drawer-footnote">
            <Chip size="small" label="Audited" />
            {' '}When a regulator asks who approved a change, the log names both of you.
          </div>
        </div>
      </Drawer>
    );
  }
}

export default DelegateDrawer;
